import { useApi } from 'ApiHooks'
import { useBlockUI } from 'Context/BlockUIContext'
import { useErrorModal } from 'Context/ErrorModalContext'
import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Modal, Pagination, Row, Table } from 'react-bootstrap'
import { storageFactory } from 'storage-factory'
import { fechaActual, fechaUnMesAtras } from 'Utilidades'
import { useIsMobile } from 'utils/Utils'

interface ModalBusquedaPedidoGuiaProps {
    show: boolean
    setShow: (show: boolean) => void
    setAgregarSeleccionados: (seleccionados: any[]) => void
    pedidosAgregados: any[]
}

const ModalBusquedaPedidoGuia = ({
    show,
    setShow,
    setAgregarSeleccionados,
    pedidosAgregados
}: ModalBusquedaPedidoGuiaProps) => {

    const api = useApi()
    const { showBlock, hideBlock } = useBlockUI()
    const { showError } = useErrorModal()
    const storage = storageFactory(() => localStorage)
    const isMobile = useIsMobile()
    const declaranteActual = storage.getItem("declaranteSeleccionado")

    const [busquedaPedidoGuiaResult, setBusquedaPedidoGuiaResult] = useState<any[]>([])
    const [busquedaPedidoGuiaResultCompleto, setBusquedaPedidoGuiaResultCompleto] = useState<any[]>([])
    const [fechaDesdePedidoGuia, setFechaDesdePedidoGuia] = useState<string>(fechaUnMesAtras())
    const [fechaHastaPedidoGuia, setFechaHastaPedidoGuia] = useState<string>(fechaActual())
    const [cuitPedidoGuia, setCuitPedidoGuia] = useState<string>('')
    const [nroDespachoPedidoGuia, setNroDespachoPedidoGuia] = useState<string>('')
    const [legajosSeleccionados, setLegajosSeleccionados] = useState<string[]>([])
    const [pageSize] = useState(50)
    const [totalRegistros, setTotalRegistros] = useState(0)
    const [base64Excel, setBase64Excel] = useState<string>("")
    const totalPaginas = Math.ceil(totalRegistros / pageSize)
    const [paginaActual, setPaginaActual] = useState(1)

    const getBusquedaPedidoGuia = async (pagina = 1) => {
        showBlock("Cargando resultados...")
        try {
            const pedidoGuiaResultados = await api.getBusquedaPedidoGuia({
                "CuitDespachante": declaranteActual,
                "desde": fechaDesdePedidoGuia,
                "hasta": fechaHastaPedidoGuia,
                "cuitIE": cuitPedidoGuia ?? null,
                "nroDespacho": nroDespachoPedidoGuia,
                "pagina": pagina,
                "orden": 1
            })
            const pedidoGuiaResultadoExcel = await api.getBusquedaPedidoGuiaExcel({
                "CuitDespachante": declaranteActual,
                "desde": fechaDesdePedidoGuia,
                "hasta": fechaHastaPedidoGuia,
                "cuitIE": cuitPedidoGuia ?? null,
                "nroDespacho": nroDespachoPedidoGuia,
                "pagina": pagina,
                "orden": 1
            })
            setBase64Excel(pedidoGuiaResultadoExcel)
            setBusquedaPedidoGuiaResult(pedidoGuiaResultados?.listaDespachoEndo || [])
            setBusquedaPedidoGuiaResultCompleto((prev) => [...prev, pedidoGuiaResultados?.listaDespachoEndo])
            setTotalRegistros(pedidoGuiaResultados?.cantidadTotalDespachos || 0)
            setPaginaActual(pagina)

        } catch (error: any) {
            if (!api.isCancel(error)) {
                showError({
                    title: 'Error al cargar datos',
                    message: error?.response?.status === 401 ? 'Se ha vencido el tiempo de la sesión' : error?.response?.data?.message?.length > 0 ? error?.response?.data?.message : 'Error desconocido del servidor',
                })
            }
        } finally {
            hideBlock()
        }
    }

    const MAX_PEDIDOS = 30

    const toggleSeleccion = (nroLegajo: string) => {
        setLegajosSeleccionados(prev => {
            if (prev.includes(nroLegajo)) {
                return prev.filter(l => l !== nroLegajo)
            }

            if (prev.length >= MAX_PEDIDOS) {
                showError({
                    title: 'Límite alcanzado',
                    message: 'No podés seleccionar más de 30 pedidos.'
                })
                return prev
            }

            return [...prev, nroLegajo]
        })
    }

    const toggleSeleccionTodos = () => {
        if (legajosSeleccionados.length === busquedaPedidoGuiaResult.length) {
            setLegajosSeleccionados([])
            return
        }

        if (busquedaPedidoGuiaResult.length > MAX_PEDIDOS) {
            showError({
                title: 'Límite alcanzado',
                message: `Solo podés seleccionar hasta ${MAX_PEDIDOS} pedidos.`
            })
            setLegajosSeleccionados(
                busquedaPedidoGuiaResult
                    .slice(0, MAX_PEDIDOS)
                    .map((lote: any) => lote.nroLegajo)
            )
            return
        }
        setLegajosSeleccionados(
            busquedaPedidoGuiaResult.map((lote: any) => lote.nroLegajo)
        )
    }

    const mapa = new Map(
        busquedaPedidoGuiaResultCompleto.flat().map(l => [l.nroLegajo, l])
    )

    const objetosSeleccionados = legajosSeleccionados
        .map(nro => mapa.get(nro))
        .filter(Boolean)

    useEffect(() => {
        if (show) {
            getBusquedaPedidoGuia()
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps    
    }, [show])

    return (
        <Modal show={show} size='lg' centered style={{ backgroundColor: 'rgba(1,1,1,0.5)' }}>
            <Modal.Header closeButton={false} >
                <Modal.Title style={{ color: 'white', fontWeight: 'bold' }}>Busqueda Pedido Guia</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Card>
                    <Card.Body className="p-0">
                        <Form>
                            <Row>
                                <Col md="auto">
                                    <label className="fw-bold mb-1"><b>Fecha ENDO Desde</b></label>
                                    <Form.Control
                                        type="date"
                                        defaultValue={fechaUnMesAtras()}
                                        value={fechaDesdePedidoGuia}
                                        onChange={(e) => setFechaDesdePedidoGuia(e.target.value)}
                                    />
                                </Col>
                                <Col md="auto" style={{ marginTop: isMobile ? 10 : 0 }}>
                                    <label className="fw-bold mb-1"><b>Fecha ENDO Hasta</b></label>
                                    <Form.Control
                                        type="date"
                                        defaultValue={fechaActual()}
                                        value={fechaHastaPedidoGuia}
                                        onChange={(e) => setFechaHastaPedidoGuia(e.target.value)}
                                    />
                                </Col>
                                <Col md="3" style={{ marginTop: isMobile ? 10 : 0 }}>
                                    <Form.Group>
                                        <label className="fw-bold mb-1"><b>Cuit IE</b></label>
                                        <Form.Control
                                            name="cuit"
                                            value={cuitPedidoGuia}
                                            onChange={(e) => setCuitPedidoGuia(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md="3" style={{ marginTop: isMobile ? 1 : 0 }}>
                                    <Form.Group>
                                        <label className="fw-bold mb-1"><b>Nro. Despacho</b></label>
                                        <Form.Control
                                            name="nroDespachoPedidoGuia"
                                            value={nroDespachoPedidoGuia}
                                            onChange={(e) => setNroDespachoPedidoGuia(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md="auto">
                                    <Button type='primary' onClick={(e) => {
                                        e?.preventDefault()
                                        getBusquedaPedidoGuia()
                                    }}>Buscar</Button>
                                    <div className="d-flex align-items-center text-muted mt-2">
                                        <span>
                                            Seleccionados: {legajosSeleccionados.length} / 30
                                        </span>

                                        {
                                            (busquedaPedidoGuiaResult?.length > 0 && base64Excel) && (
                                                <Button
                                                    variant="success"
                                                    className="ml-3"
                                                    onClick={() => {
                                                        const link = document.createElement("a")
                                                        link.href = `data:application/vnd.ms-excel;base64,${base64Excel}`
                                                        link.download = `pedio_guia_${new Date().toISOString().slice(0, 10)}.xlsx`
                                                        document.body.appendChild(link)
                                                        link.click()
                                                        document.body.removeChild(link)
                                                    }}
                                                >
                                                    Descargar Excel
                                                </Button>
                                            )
                                        }
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
                {busquedaPedidoGuiaResult?.length === 0 ? (
                    <div className="p-4 text-center text-muted mt-2">
                        <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                        No hay registros para mostrar
                    </div>
                ) : isMobile ? (<div className="d-md-none mt-2">
                    <Card className="mb-2 shadow-sm" style={{ backgroundColor: 'white' }}>
                        <Card.Body className="d-flex align-items-center">
                            <input
                                type="checkbox"
                                className="mr-2"
                                checked={
                                    busquedaPedidoGuiaResult?.length > 0 &&
                                    legajosSeleccionados.length === busquedaPedidoGuiaResult.length
                                }
                                onChange={toggleSeleccionTodos}
                            />
                            <strong>Seleccionar todos</strong>
                        </Card.Body>
                    </Card>
                    {busquedaPedidoGuiaResult?.filter(item =>
                        !pedidosAgregados?.some(ex => ex.nroLegajo === item.nroLegajo)
                    )?.map((lote: any) => (
                        <Card key={lote.generadorId} className="mb-2 shadow-sm" style={{ backgroundColor: 'white' }}>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                        <h6 className="mb-0">{lote.nroLegajo}</h6>
                                        <small className="text-muted">{lote.codigo}</small>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={legajosSeleccionados.includes(lote.nroLegajo)}
                                        onChange={() => toggleSeleccion(lote.nroLegajo)}
                                        disabled={
                                            !legajosSeleccionados.includes(lote.nroLegajo) &&
                                            legajosSeleccionados.length >= 30
                                        }
                                    />
                                </div>

                                <div className="mb-1">
                                    <strong>CUIT:</strong> {lote.cuitIE}
                                </div>

                                <div className="mb-1">
                                    <strong>Nombre:</strong> {lote.descIE}
                                </div>

                                <div className="mb-1">
                                    <strong>Fecha ENDO:</strong>{" "}
                                    {lote.fechaEndo
                                        ? new Date(lote.fechaEndo).toLocaleDateString()
                                        : "N/A"}
                                </div>

                                <div className="mb-1">
                                    <strong>SIGEA:</strong> {lote.sigea}
                                </div>

                                <div>
                                    <strong>ID:</strong> {lote.generadorId}
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                    {totalPaginas > 1 && (
                        <Pagination className="justify-content-center mt-3">
                            <Pagination.First
                                disabled={paginaActual === 1}
                                onClick={() => getBusquedaPedidoGuia(1)}
                            />

                            <Pagination.Prev
                                disabled={paginaActual === 1}
                                onClick={() => getBusquedaPedidoGuia(paginaActual - 1)}
                            />

                            {[...Array(totalPaginas)].map((_, i) => (
                                <Pagination.Item
                                    key={i + 1}
                                    active={paginaActual === i + 1}
                                    onClick={() => getBusquedaPedidoGuia(i + 1)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            ))}

                            <Pagination.Next
                                disabled={paginaActual === totalPaginas}
                                onClick={() => getBusquedaPedidoGuia(paginaActual + 1)}
                            />

                            <Pagination.Last
                                disabled={paginaActual === totalPaginas}
                                onClick={() => getBusquedaPedidoGuia(totalPaginas)}
                            />
                        </Pagination>
                    )}
                </div>
                ) : (
                    <div className="table-responsive lotes-table mt-2">
                        <Table hover size="sm" className="mb-0">
                            <thead className="thead-light">
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={
                                                busquedaPedidoGuiaResult?.length > 0 &&
                                                legajosSeleccionados.length === busquedaPedidoGuiaResult.length
                                            }
                                            onChange={toggleSeleccionTodos}
                                        />
                                    </th>
                                    <th>Despacho</th>
                                    <th>Código</th>
                                    <th>Cuit Impo/Expo</th>
                                    <th>Nombre Impo/Expo</th>
                                    <th>Fecha ENDO</th>
                                    <th>SIGEA</th>
                                    <th>ID</th>
                                </tr>
                            </thead>

                            <tbody>
                                {busquedaPedidoGuiaResult?.filter(item =>
                                    !pedidosAgregados?.some(ex => ex.nroLegajo === item.nroLegajo)
                                )?.map((lote: any) => (
                                    <tr key={lote.generadorId}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={legajosSeleccionados.includes(lote.nroLegajo)}
                                                onChange={() => toggleSeleccion(lote.nroLegajo)}
                                                disabled={
                                                    !legajosSeleccionados.includes(lote.nroLegajo) &&
                                                    legajosSeleccionados.length >= 30
                                                }
                                            />
                                        </td>
                                        <td>{lote.nroLegajo}</td>
                                        <td>{lote.codigo}</td>
                                        <td>{lote.cuitIE}</td>
                                        <td>{lote.descIE}</td>
                                        <td>
                                            {lote.fechaEndo
                                                ?? "N/A"}
                                        </td>
                                        <td>{lote.sigea}</td>
                                        <td>{lote.generadorId}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                    </div>
                )}
                {totalPaginas > 1 && (
                    <Pagination className="justify-content-center mt-3">
                        <Pagination.First
                            disabled={paginaActual === 1}
                            onClick={() => getBusquedaPedidoGuia(1)}
                        />

                        <Pagination.Prev
                            disabled={paginaActual === 1}
                            onClick={() => getBusquedaPedidoGuia(paginaActual - 1)}
                        />

                        {[...Array(totalPaginas)].map((_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={paginaActual === i + 1}
                                onClick={() => getBusquedaPedidoGuia(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}

                        <Pagination.Next
                            disabled={paginaActual === totalPaginas}
                            onClick={() => getBusquedaPedidoGuia(paginaActual + 1)}
                        />

                        <Pagination.Last
                            disabled={paginaActual === totalPaginas}
                            onClick={() => getBusquedaPedidoGuia(totalPaginas)}
                        />
                    </Pagination>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant={legajosSeleccionados.length === 0 ? "secondary" : "primary"}
                    disabled={legajosSeleccionados.length === 0}
                    onClick={() => {
                        setAgregarSeleccionados(objetosSeleccionados)
                        setLegajosSeleccionados([])
                        setShow(false)
                    }}
                >
                    Agregar
                </Button>
                <Button variant="danger" onClick={() => {
                    setLegajosSeleccionados([])
                    setShow(false)
                }}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ModalBusquedaPedidoGuia
